import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

/**
 * Per-action_type parameter editor — extracted from AutomationRuleDialog's
 * former renderActionParameters so the flat form and the flow-graph editor
 * (AutomationFlowNodes.tsx) share one implementation instead of drifting.
 */
interface Props {
  actionType: string;
  params: Record<string, any>;
  onChange: (paramKey: string, value: any) => void;
  isTicketEvent: boolean;
  agents: any[];
  teams: any[];
  labels: any[];
  // 2026-08-09: notify_agent/notify_manager's optional per-rule template/
  // channel override — same option shape AgentNotificationSettings.tsx uses.
  templates: Array<{ id: number; name: string; language?: string }>;
  channels: Array<{ id: number; name: string }>;
  idPrefix: string;
}

const AutomationActionParamsEditor: React.FC<Props> = ({
  actionType, params, onChange, isTicketEvent, agents, teams, labels, templates, channels, idPrefix,
}) => {
  switch (actionType) {
    case 'assign_agent':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Agent</Label>
          <Select
            value={params.agent_id != null && params.agent_id !== '' ? String(params.agent_id) : ''}
            onValueChange={(v) => onChange('agent_id', v === '' ? '' : Number(v))}
          >
            <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
            <SelectContent>
              {agents.map((agent) => (
                <SelectItem key={agent.id} value={String(agent.id)}>
                  {agent.first_name} {agent.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'assign_team':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Team</Label>
          <Select
            value={params.team_id != null && params.team_id !== '' ? String(params.team_id) : ''}
            onValueChange={(v) => onChange('team_id', v === '' ? '' : Number(v))}
          >
            <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
            <SelectContent>
              {teams.map((team) => (
                <SelectItem key={team.id} value={String(team.id)}>{team.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'add_label':
    case 'remove_label':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Label</Label>
          <Select
            value={params.label_id != null && params.label_id !== '' ? String(params.label_id) : ''}
            onValueChange={(v) => onChange('label_id', v === '' ? '' : Number(v))}
          >
            <SelectTrigger><SelectValue placeholder="Select label" /></SelectTrigger>
            <SelectContent>
              {labels.map((label) => (
                <SelectItem key={label.id} value={String(label.id)}>{label.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'change_status':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Status</Label>
          <Select value={params.status || ''} onValueChange={(v) => onChange('status', v)}>
            <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent>
              {isTicketEvent ? (
                <>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting_on_customer">Waiting On Customer</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      );

    case 'change_priority':
      return (
        <div className="space-y-2">
          <Label className="text-xs">Priority</Label>
          <Select value={params.priority || ''} onValueChange={(v) => onChange('priority', v)}>
            <SelectTrigger><SelectValue placeholder="Select priority" /></SelectTrigger>
            <SelectContent>
              {isTicketEvent ? (
                <>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      );

    case 'send_message':
      return (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-msg`} className="text-xs">Message</Label>
          <Textarea
            id={`${idPrefix}-msg`}
            rows={3}
            value={params.message || ''}
            onChange={(e) => onChange('message', e.target.value)}
            placeholder="Message to send…"
          />
        </div>
      );

    case 'notify_agent':
    case 'notify_manager':
      return (
        <div className="space-y-3">
          <Label htmlFor={`${idPrefix}-notify-msg`} className="text-xs">Message (optional)</Label>
          <Textarea
            id={`${idPrefix}-notify-msg`}
            rows={2}
            value={params.message || ''}
            onChange={(e) => onChange('message', e.target.value)}
            placeholder="Extra context for the notification…"
          />
          {/* 2026-08-09: overrides the account-wide default (Automation ->
              Agent notifications) for just this rule — different tickets
              can warrant a different template/number, e.g. a crisis
              escalation vs a routine assignment. */}
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Template (optional override)</Label>
              <Select
                value={params.template_id != null ? String(params.template_id) : 'default'}
                onValueChange={(v) => onChange('template_id', v === 'default' ? null : Number(v))}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Account default</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}{t.language ? ` · ${t.language}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Channel (optional override)</Label>
              <Select
                value={params.channel_id != null ? String(params.channel_id) : 'default'}
                onValueChange={(v) => onChange('channel_id', v === 'default' ? null : Number(v))}
              >
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Account default</SelectItem>
                  {channels.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      );

    case 'escalate_ticket':
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs">Escalate to priority</Label>
            <Select value={params.priority || 'high'} onValueChange={(v) => onChange('priority', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-escalate-reason`} className="text-xs">Reason (optional)</Label>
            <Input
              id={`${idPrefix}-escalate-reason`}
              value={params.reason || ''}
              onChange={(e) => onChange('reason', e.target.value)}
            />
          </div>
        </div>
      );

    case 'create_follow_up_ticket':
      return (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-followup-cat`} className="text-xs">Follow-up category ID (optional)</Label>
          <Input
            id={`${idPrefix}-followup-cat`}
            value={params.category_id || ''}
            onChange={(e) => onChange('category_id', e.target.value)}
            placeholder="Same category if left blank"
          />
        </div>
      );

    case 'set_ai_enabled':
      // 2026-08-10: Docs/DIGITAL_COMPANION_AI_HANDOFF_PLAN.md §2.7 — turns AI
      // on/off for the conversation behind whichever ticket/event triggered
      // this rule. Idempotent on the backend, so re-running the rule is safe.
      return (
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs">AI</Label>
            <Select
              value={params.enabled === false ? 'off' : 'on'}
              onValueChange={(v) => onChange('enabled', v === 'on')}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Turn off (hand off to a human)</SelectItem>
                <SelectItem value="on">Turn on</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${idPrefix}-ai-reason`} className="text-xs">Reason (optional)</Label>
            <Input
              id={`${idPrefix}-ai-reason`}
              value={params.reason || ''}
              onChange={(e) => onChange('reason', e.target.value)}
              placeholder="Shown in the conversation's escalation history"
            />
          </div>
        </div>
      );

    case 'wait': {
      // Stored as a single duration_minutes int (matches the
      // sla_response_minutes convention used elsewhere in this app) — the
      // unit selector here is just a display convenience that converts to/
      // from minutes on the way in and out.
      const totalMinutes = Number(params.duration_minutes) || 0;
      const unit: 'minutes' | 'hours' | 'days' =
        totalMinutes > 0 && totalMinutes % 1440 === 0 ? 'days'
        : totalMinutes > 0 && totalMinutes % 60 === 0 ? 'hours'
        : 'minutes';
      const unitDivisor = unit === 'days' ? 1440 : unit === 'hours' ? 60 : 1;
      const amount = unit === 'minutes' ? totalMinutes : totalMinutes / unitDivisor;
      return (
        <div className="space-y-2">
          <Label className="text-xs">Wait for</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              min={1}
              className="w-24"
              value={amount || ''}
              onChange={(e) => {
                const n = Number(e.target.value) || 0;
                onChange('duration_minutes', n * unitDivisor);
              }}
            />
            <Select
              value={unit}
              onValueChange={(v) => {
                const divisor = v === 'days' ? 1440 : v === 'hours' ? 60 : 1;
                onChange('duration_minutes', (amount || 0) * divisor);
              }}
            >
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">Minutes</SelectItem>
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="days">Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            Pauses here, then continues with whatever comes next in this chain.
          </p>
        </div>
      );
    }

    default:
      return null;
  }
};

export default AutomationActionParamsEditor;
